import { useState, useRef, useEffect, KeyboardEvent, FC } from "react";
import { Show } from "shared/components";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import { ZipCodeRangeCard } from "./ZipCodeRangeCard";
import { SelectedOptionsBadges } from "./SelectedOptionsBadges";
import { BoomPropertiesOptionsList } from "./BoomPropertiesOptionsList";
import { ExternalOptionsList } from "./ExternalOptionsList";
import { ChevronDown } from "untitledui-js-base";
import { MutiSelectMode } from "../type";
import { PropertyGroupSplitBy } from "views/property-groups-configuration/types";
import { cx } from "shared/utils";
import { propertyGroupTitleMap } from "views/property-groups-configuration/constants";
import { plural } from "pluralize";
// eslint-disable-next-line @typescript-eslint/no-require-imports -- Rolldown CJS interop doesn't unwrap __esModule default
import _usePlacesModule from "react-google-autocomplete/lib/usePlacesAutocompleteService";

const useGoogle: typeof _usePlacesModule =
  (_usePlacesModule as { default?: typeof _usePlacesModule }).default ??
  _usePlacesModule;
import config from "config/config";
import { getStateLabel, states } from "utils/stateCodes";

interface PropertyGroupMutiSelectProps {
  id: string;
  label: string;
  options: string[];
  placeholder?: string;
  selectedOptions: string[];
  disabledOptions?: string[];
  onSelect?: (options: string[]) => void;
  mode?: MutiSelectMode;
  currentStep: PropertyGroupSplitBy;
  error?: string;
  segmentName?: string;
  optionsWithSegmentMap?: Record<string, string>;
  isLastStep?: boolean;
}

export const PropertyGroupMultiSelect: FC<PropertyGroupMutiSelectProps> = ({
  id,
  label,
  options,
  placeholder = "Select options",
  selectedOptions,
  disabledOptions = [],
  onSelect,
  mode = MutiSelectMode.LIST,
  currentStep,
  error,
  segmentName,
  optionsWithSegmentMap,
  isLastStep
}) => {
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);
  const [stateFilteredOptions, setStateFilteredOptions] = useState<string[]>(
    []
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const isZipCodeMode = currentStep === PropertyGroupSplitBy.ZIP;
  const isStateMode = currentStep === PropertyGroupSplitBy.STATE;
  const zipPlaceholder = "Add ZIP codes";
  const displayPlaceholder = isZipCodeMode ? zipPlaceholder : placeholder;

  const { placePredictions, getPlacePredictions } = useGoogle({
    libraries: ["places"],
    apiKey: config.placesApiKey,
    debounce: 200,
    options: {
      input: "",
      language: "en",
      types: ["(cities)"],

      componentRestrictions: { country: "us" }
    }
  });

  useEffect(() => {
    if (!isStateMode) return;

    const stateList = inputValue
      ? states
          .filter(
            (state) =>
              state.label.toLowerCase().includes(inputValue.toLowerCase()) ||
              state.value.toLowerCase().includes(inputValue.toLowerCase())
          )
          .map((state) => state.value)
      : states.map((state) => state.value);

    setStateFilteredOptions(stateList);
  }, [inputValue, isStateMode]);

  const focusInput = () => {
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  useEffect(() => {
    if (open && inputRef.current) focusInput();
  }, [open]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setInputError(null);

    if (currentStep === PropertyGroupSplitBy.CITY)
      getPlacePredictions({
        input: e.target.value,
        componentRestrictions: { country: "us" }
      });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (
      (currentStep === PropertyGroupSplitBy.OWNER ||
        currentStep === PropertyGroupSplitBy.MARKET) &&
      e.key === "Enter" &&
      inputValue.trim()
    ) {
      if (disabledOptions.includes(inputValue.trim())) {
        const segmentWithValue =
          optionsWithSegmentMap?.[inputValue.trim()] || "another";
        setInputError(
          `"${inputValue.trim()}" already exists in "${segmentWithValue}" ${isLastStep ? "group" : "segment"}`
        );
        setOpen(false);
        e.preventDefault();
        return;
      }

      setInputError(null);
      const newOptionsSet = new Set([...selectedOptions, inputValue.trim()]);
      onSelect?.(Array.from(newOptionsSet));
      setInputValue("");
      e.preventDefault();
    }

    if (e.key === " " || e.key === "Tab") e.stopPropagation();
  };

  const handleOptionSelect = (option: string) => {
    if (disabledOptions.includes(option)) return;
    onSelect?.([...selectedOptions, option]);
    setInputValue("");
    focusInput();
  };

  const handlePredictionSelect = (predictionText: string) => {
    onSelect?.([...selectedOptions, predictionText]);
    setInputValue("");
    focusInput();
  };

  const removeOption = (option: string) => {
    onSelect?.(selectedOptions.filter((item) => item !== option));
    focusInput();
  };

  const getDisplayValue = (value: string): string => {
    if (isStateMode) return getStateLabel(value);
    return value;
  };

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-content-700 text-xs font-medium">
        {label}
      </label>
      <Popover
        placement="bottom-start"
        open={open}
        onOpenChange={(v) => {
          onSelect?.(selectedOptions);
          setOpen(v);
          setInputError(null);
          if (v && !isZipCodeMode && inputRef.current) focusInput();
        }}
      >
        <PopoverTrigger
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setOpen(!open);
            if (!isZipCodeMode && inputRef.current) focusInput();
          }}
          className={cx(
            "flex min-h-10 gap-2 w-full justify-between items-center py-2 px-[14px] bg-white rounded-lg border shadow-xs group cursor-pointer",
            error || inputError ? "border-danger-300" : "border-alpha-100"
          )}
        >
          <div className="flex gap-2 flex-wrap w-full">
            <Show if={selectedOptions.length > 0}>
              <SelectedOptionsBadges
                selectedOptions={selectedOptions}
                open={open}
                removeOption={removeOption}
                getDisplayValue={getDisplayValue}
                setOpen={setOpen}
              />
            </Show>

            <Show if={(open || !selectedOptions.length) && !isZipCodeMode}>
              <input
                ref={inputRef}
                placeholder={
                  selectedOptions.length > 0 ? "" : displayPlaceholder
                }
                className="border-none outline-none focus:ring-0 p-0 m-0 min-w-[10px] placeholder:text-content-100"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setOpen(true)}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(true);
                }}
                onBlur={(e) => {
                  if (e.currentTarget.contains(e.relatedTarget as Node)) {
                    e.preventDefault();
                    focusInput();
                  }
                }}
              />
            </Show>

            <Show if={!selectedOptions.length && !open && isZipCodeMode}>
              <span className="text-content-100 text-sm">
                {displayPlaceholder}
              </span>
            </Show>
          </div>
          <ChevronDown className="stroke-content-500 ml-auto size-4" />
        </PopoverTrigger>

        <PopoverContent className="z-[50]">
          <Show if={isZipCodeMode}>
            <ZipCodeRangeCard
              options={options}
              usedZipCodes={disabledOptions}
              segmentName={segmentName}
              optionsWithSegmentMap={optionsWithSegmentMap}
              onClose={() => setOpen(false)}
              onSelect={onSelect}
              selectedOptions={selectedOptions}
            />
          </Show>

          <Show if={!isZipCodeMode && mode === MutiSelectMode.LIST}>
            <div className="animate-bounce-in border border-alpha-100 rounded-lg bg-white shadow-xl w-[340px] flex flex-col max-h-[400px] overflow-y-auto px-6 py-5">
              <p className="pb-2 border-b border-alpha-40 text-xs font-medium text-content-200">
                From properties in Boom
              </p>

              <div className="my-2 w-full">
                <BoomPropertiesOptionsList
                  options={options}
                  inputValue={inputValue}
                  selectedOptions={selectedOptions}
                  disabledOptions={disabledOptions}
                  currentStep={currentStep}
                  optionsWithSegmentMap={optionsWithSegmentMap}
                  segmentName={segmentName}
                  isLastStep={isLastStep}
                  onOptionSelect={handleOptionSelect}
                />
              </div>

              <Show
                if={
                  ![
                    PropertyGroupSplitBy.OWNER,
                    PropertyGroupSplitBy.MARKET
                  ].includes(currentStep)
                }
              >
                <p className="pb-2 mb-2 border-b border-alpha-40 text-xs font-medium text-content-200">
                  Other {plural(propertyGroupTitleMap[currentStep])}
                </p>

                <ExternalOptionsList
                  options={options}
                  inputValue={inputValue}
                  selectedOptions={selectedOptions}
                  disabledOptions={disabledOptions}
                  currentStep={currentStep}
                  isStateMode={isStateMode}
                  stateFilteredOptions={stateFilteredOptions}
                  placePredictions={placePredictions}
                  onOptionSelect={handleOptionSelect}
                  onPredictionSelect={handlePredictionSelect}
                />
              </Show>
            </div>
          </Show>
        </PopoverContent>
      </Popover>
      <Show if={error || inputError}>
        <p className="mt-1 text-xs text-danger-500">{inputError || error}</p>
      </Show>
    </div>
  );
};
